import { useDocumentDirection, useTranslation } from '@multaqa/i18n'
import { AppError, AppErrorMessagesProvider } from '@multaqa/ui/components/async/app-error'
import { Toaster } from '@multaqa/ui/components/ui/sonner'
import { useDocumentTitle } from '@multaqa/ui/hooks/use-document-title'
import type { QueryClient } from '@tanstack/react-query'
import type { ErrorComponentProps } from '@tanstack/react-router'
import { createRootRouteWithContext, Outlet, useRouter } from '@tanstack/react-router'
import { lazy, Suspense } from 'react'
import { AppErrorBoundary } from '@/components/app-error-boundary'

interface RouterContext {
  queryClient: QueryClient
}

/**
 * Devtools are lazy and development-only, so they are absent from the
 * production bundle entirely rather than merely hidden.
 */
const Devtools = import.meta.env.PROD
  ? () => null
  : lazy(async () => {
      const [router, query] = await Promise.all([
        import('@tanstack/react-router-devtools'),
        import('@tanstack/react-query-devtools'),
      ])

      return {
        default: () => (
          <>
            <router.TanStackRouterDevtools position="bottom-right" />
            <query.ReactQueryDevtools initialIsOpen={false} />
          </>
        ),
      }
    })

function RootLayout() {
  const { t } = useTranslation()
  const messages = {
    title: {
      generic: t('error.generic.title'),
      network: t('error.network.title'),
      unauthorized: t('error.unauthorized.title'),
      suspended: t('error.suspended.title'),
      forbidden: t('error.forbidden.title'),
      notFound: t('error.notFound.title'),
      conflict: t('error.conflict.title'),
      rateLimited: t('error.rateLimited.title'),
    },
    body: {
      generic: t('error.generic.body'),
      network: t('error.network.body'),
      unauthorized: t('error.unauthorized.body'),
      suspended: t('error.suspended.body'),
      forbidden: t('error.forbidden.body'),
      notFound: t('error.notFound.body'),
      rateLimited: t('error.rateLimited.body'),
    },
    reference: t('error.reference'),
    referenceHint: t('error.referenceHint'),
    details: t('error.details'),
    retry: t('action.retry'),
    reload: t('action.reload'),
    signIn: t('action.signIn'),
    back: t('action.goBack'),
    copy: t('action.copy'),
    copied: t('action.copied'),
    loading: t('state.loading'),
    support: t('error.openSupport'),
  }

  // Keeps <html lang> and <html dir> matched to the active locale. Must live at
  // the root so portalled content (dialogs, toasts) inherits direction too.
  useDocumentDirection()
  // Document title follows the active locale's brand string; index.html's
  // static <title> is the pre-hydration fallback.
  useDocumentTitle(t('app.admin'))

  return (
    <>
      <AppErrorMessagesProvider messages={messages}>
        <AppErrorBoundary messages={messages}>
          <Outlet />
        </AppErrorBoundary>
      </AppErrorMessagesProvider>
      <Toaster position="top-center" richColors closeButton />
      <Suspense fallback={null}>
        <Devtools />
      </Suspense>
    </>
  )
}

function RouterError({ error, reset }: ErrorComponentProps) {
  const { t } = useTranslation()
  const router = useRouter()
  const messages = {
    title: {
      generic: t('error.generic.title'),
      network: t('error.network.title'),
      unauthorized: t('error.unauthorized.title'),
      suspended: t('error.suspended.title'),
      forbidden: t('error.forbidden.title'),
      notFound: t('error.notFound.title'),
      conflict: t('error.conflict.title'),
      rateLimited: t('error.rateLimited.title'),
    },
    body: {
      generic: t('error.generic.body'),
      network: t('error.network.body'),
      unauthorized: t('error.unauthorized.body'),
      suspended: t('error.suspended.body'),
      forbidden: t('error.forbidden.body'),
      notFound: t('error.notFound.body'),
      rateLimited: t('error.rateLimited.body'),
    },
    reference: t('error.reference'),
    referenceHint: t('error.referenceHint'),
    details: t('error.details'),
    retry: t('action.retry'),
    reload: t('action.reload'),
    signIn: t('action.signIn'),
    back: t('action.goBack'),
    copy: t('action.copy'),
    copied: t('action.copied'),
    loading: t('state.loading'),
    support: t('error.openSupport'),
  }
  return (
    <AppError
      error={error}
      messages={messages}
      retry={() => {
        reset()
        void router.invalidate()
      }}
    />
  )
}

export const Route = createRootRouteWithContext<RouterContext>()({
  component: RootLayout,
  errorComponent: RouterError,
})
